import React, { Component } from "react";
import { connect } from "react-redux";
import { deviceDetect } from "react-device-detect";
import { Redirect, Route, Switch, withRouter } from "react-router-dom";
import _ from "lodash";

import {
  selectIsACPEligible,
  selectIsTeensAndKidsProgram,
} from "@/redux/selectors/patientSelectors";
import { api } from "@/APIRequests";
import { baseRoute } from "@/config/roles";
import { BrandedHeader } from "@/components/Common/BrandedHeader/BrandedHeader";
import { fetchPatientPaywall } from "@/redux/actions/patientCardInfo";
import { Footer } from "@/component-library/Footer";
import { getAssessmentStatus, updateScheduleList } from "@/redux/actions/patient";
import { getPatientProfile, profileFetchSuccess } from "@/redux/actions/auth";
import { Main2 } from "@/ui2/atoms/Main";
import ActionSection from "@/components/Navigation/Toolbar/ActionSection/ActionSection";
import AVCheck from "@/components/Common/AVCheck";
import ChangePassword from "@/components/Authentication/ChangePassword";
import Layout from "@/hoc/Layout/Layout";
import MemberHome from "@/components/Patient/MemberHome";
import MessagesView from "@/components/Common/MessagesView";
import MobileAddPayment from "@/components/Common/MobileAddPayment";
import PrivacyPolicy from "@/components/Common/PrivacyPolicy";
import Spinner from "@/components/UI/Spinner";
import store from "@/redux/store";
import Survey from "@/components/Patient/NinetyDayCareProgressGuide/SurveyWrapper/Survey";
import SystemCheck from "@/components/Common/SystemCheck";
import TeensAndKidsCareProgram from "@/components/Patient/TeensAndKidsCareProgram";
import Toolbar from "@/components/Navigation/Toolbar/Toolbar";
import TutorialScreens from "@/components/Common/TutorialScreen";

import VideoClient from "../../VideoClient";
import mapLabelToComponent from "../common/mapLabelToComponent";

import { LogInTasksRouter } from "./acknowledgements/LogInTasks/LoginTasksRouter";
import { PaymentPaywall } from "./acknowledgements/PaymentPaywall/PaymentPaywall";
import { PolicyAcknowledgement } from "./acknowledgements/PolicyAcknowledgement/PolicyAcknowledgement";

import { PatientConsents } from "./PatientConsents";
import AppBanner from "./AppBanner";
import Assessment from "./Assessment";
import GAD7 from "./GAD7";
import PatientInsurance from "./PatientInsurance";
import PatientNotifications from "./PatientNotifications";
import PaymentsPage from "./PaymentsPage/PaymentsPage";
import PHQ9 from "./PHQ9";
import PostSessionSurveyWrapper from "./PostSessionSurveyWrapper";
import Profile from "./PatientProfile";
import ScheduleRouter from "./ScheduleRouter";
import SettingsPage from "./Settings";

import "./styles.scss";

class Patient extends Component {
  deviceInfo = deviceDetect();

  ROUTE_SECTION_OPTIONS = [
    {
      label: "Home",
      icon: "NavMenu__home-icon",
      mobileIcon: "member_home.svg",
      route: this.props.url + "/home",
      iconComponent: mapLabelToComponent("Home"),
      activeIconComponent: mapLabelToComponent("Home", true),
    },
    {
      label: "Schedule",
      icon: "NavMenu__schedule-icon",
      mobileIcon: "member_schedule.svg",
      route: this.props.url + "/schedule",
      iconComponent: mapLabelToComponent("Schedule"),
      activeIconComponent: mapLabelToComponent("Schedule", true),
    },
    {
      label: "Messages",
      icon: "NavMenu__messages-icon",
      mobileIcon: "member_messages.svg",
      route: this.props.url + "/messages",
      iconComponent: mapLabelToComponent("Messages"),
      activeIconComponent: mapLabelToComponent("Messages", true),
    },
  ];

  ACTION_SECTION_OPTIONS = [];

  navbarConfiguration = {
    routeSection: {
      options: this.ROUTE_SECTION_OPTIONS,
    },
    customRenderSection: [
      {
        isComponent: true,
        render: ActionSection,
        componentProps: {
          config: {
            options: this.ACTION_SECTION_OPTIONS,
          },
        },
      },
    ],
    selfSection: {},
  };

  state = {
    acknowledgements: null,
    incompleteAcknowledgements: null,
    logInTasks: [],
    scheduleList: null,
    showMobileAppBanner: false,
    showPaywall: false,
    showSideDrawyer: false,
    skippedLoginTasks: new Set(), // Track skipped tasks by task_type during session
    totalUnreadMessages: 0,
  };

  async componentDidMount() {
    const { getPatientProfile, getAssessmentStatus, fetchPatientPaywall } = this.props;
    await Promise.allSettled([getPatientProfile(), fetchPatientPaywall()]);

    if (
      this.props.profile &&
      (Object.hasOwn(this.props.profile, "acknowledgements") || this.props.profile.tasks.length)
    ) {
      this.processProfileAcknowledgements(this.props.profile);
    }

    getAssessmentStatus();
    this.updateUnreadCount();
    this.checkForMobileApp();

    if (process.env.REACT_APP_ENABLE_MESSAGE_NOTIFICATIONS_UPDATE === "true") {
      let time = process.env.REACT_APP_ENABLE_MESSAGE_NOTIFICATIONS_UPDATE_REFRESH_TIME;
      this.notification = setInterval(() => {
        if (document.hidden) {
          console.log("Document is currently hidden. Waiting to fetch unread message count.");
          return;
        }
        this.updateUnreadCount();
      }, parseInt(time));
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      !prevProps.profile &&
      this.props.profile &&
      (Object.hasOwn(this.props.profile, "acknowledgements") || this.props.profile.tasks.length)
    ) {
      this.processProfileAcknowledgements(this.props.profile);
    }

    if (!prevProps.patientCardInfo.showPaywall && this.props.patientCardInfo.showPaywall) {
      this.setState({
        ...this.state,
        showPaywall: true,
      });
    }

    if (this.isAcknowledgementNeeded(prevProps, prevState)) {
      this.props.history.push(
        `/app/acknowledgments/${Object.keys(this.state.incompleteAcknowledgements)[0]}`,
      );
    } else if (this.isLogInTasksNeeded(prevProps, prevState)) {
      this.props.history.push(`/app/log-in-tasks/${this.state.logInTasks[0]?.task_type}`);
    } else if (this.isPaywallNeeded()) {
      this.props.history.push("/app/payment-check");
    }
  }

  isPaywallNeeded = () => {
    return (
      !this.state.incompleteAcknowledgements &&
      !this.state.logInTasks.length &&
      this.state.showPaywall &&
      window.location.pathname !== "/app/payment-check"
    );
  };

  isLogInTasksNeeded = (_prevProps, prevState) => {
    if (!prevState.logInTasks.length && this.state.logInTasks.length) {
      return true;
    }

    if (
      prevState.logInTasks.length &&
      this.state.logInTasks.length &&
      !_.isEqual(prevState.logInTasks.length, this.state.logInTasks.length)
    ) {
      return true;
    }

    // Account for finishing acknowledgments
    if (
      prevState.incompleteAcknowledgements !== undefined &&
      this.state.incompleteAcknowledgements === undefined &&
      this.state.logInTasks.length
    ) {
      return true;
    }
  };

  processProfileAcknowledgements(profile) {
    const acknowledgements = profile.acknowledgements;
    const newState = {};
    const incompleteAcknowledgements = this.getIncompleteAcknowledgements(acknowledgements);
    const incompleteLogInTasks = this.getIncompleteLogInTasks(profile);

    if (!_.isNull(incompleteAcknowledgements)) {
      newState["incompleteAcknowledgements"] = incompleteAcknowledgements;
      newState["acknowledgements"] = acknowledgements;
    }

    if (incompleteLogInTasks.length) {
      newState["logInTasks"] = incompleteLogInTasks;
    }

    if (!_.isEmpty(newState)) {
      this.setState(newState);
    }
  }

  isAcknowledgementNeeded = (_prevProps, prevState) => {
    if (!prevState.incompleteAcknowledgements && this.state.incompleteAcknowledgements) {
      return true;
    }

    if (
      prevState.incompleteAcknowledgements &&
      this.state.incompleteAcknowledgements &&
      !_.isEqual(prevState.incompleteAcknowledgements, this.state.incompleteAcknowledgements)
    ) {
      return true;
    }
  };

  getIncompleteAcknowledgements(acknowledgements) {
    if (_.isNull(acknowledgements) && _.isEmpty(acknowledgements)) {
      return;
    }

    const incompleteAcknowledgements = Object.entries(acknowledgements)
      .filter(([, ackVal]) => ackVal === null)
      .reduce((obj, [key, value]) => {
        obj[key] = value;
        return obj;
      }, {});

    if (_.isEmpty(incompleteAcknowledgements)) {
      return;
    }

    return incompleteAcknowledgements;
  }

  onAcknowledgementComplete = (currentAcknowledgement, acknowledgementData) => {
    let incompleteAcknowledgements = {
      ...this.state.incompleteAcknowledgements,
    };
    delete incompleteAcknowledgements[currentAcknowledgement];
    incompleteAcknowledgements = this.getIncompleteAcknowledgements(incompleteAcknowledgements);

    const acknowledgements = { ...this.state.acknowledgements };
    acknowledgements[currentAcknowledgement] = acknowledgementData;

    store.dispatch(
      profileFetchSuccess({
        ...this.props.profile,
        acknowledgements,
      }),
    );

    this.setState({ incompleteAcknowledgements, acknowledgements });
    this.canPushToHome();
  };

  onTaskCompleted = (task) => {
    this.removeLogInTask(task);
  };

  onTaskSkipped = (task) => {
    // Add task_type to skipped set to prevent re-showing during this session
    this.setState(
      (prevState) => ({
        skippedLoginTasks: new Set(prevState.skippedLoginTasks).add(task.task_type),
      }),
      () => {
        // Remove from current task list to allow navigation
        this.removeLogInTask(task);
      },
    );
  };

  canPushToHome = () => {
    if (
      !this.state.incompleteAcknowledgements &&
      !this.state.logInTasks.length &&
      !this.state.showPaywall
    ) {
      window.localStorage.removeItem("redirectAfterAuth");
      this.props.history.push("/app/home");
    }
  };

  removeLogInTask = (task) => {
    const tasks = this.state.logInTasks;
    const { policy_type, target_patient_id, task_type } = task;
    const updatedTasks = tasks.filter(
      (item) =>
        !(
          item.policy_type === policy_type &&
          item.target_patient_id === target_patient_id &&
          item.task_type === task_type
        ),
    );

    if (!updatedTasks.length) {
      store.dispatch(
        profileFetchSuccess({
          ...this.props.profile,
          tasks: this.filterOutLogInTasks(this.props.profile.tasks),
        }),
      );
    }

    this.setState({ logInTasks: updatedTasks }, () => {
      this.canPushToHome();
    });
  };

  filterOutLogInTasks = (tasks) => {
    return tasks.filter((item) => !item.display_log_in);
  };

  componentWillUnmount() {
    if (this.notification) {
      clearInterval(this.notification);
    }
  }

  async updateUnreadCount() {
    const options = { params: { showLoader: false } };
    let count = await api.messaging.fetch_unread_message_count({ options });
    this.setState({
      totalUnreadMessages: count["unread_count"],
    });
  }

  toggleSideDrawyer = () => {
    this.setState((prevState) => {
      return {
        showSideDrawyer: !prevState.showSideDrawyer,
      };
    });
  };

  onPaywallCompleted = () => {
    this.setState({
      ...this.state,
      showPaywall: false,
    });

    this.props.history.push("/app");
  };

  checkForMobileApp = async () => {
    try {
      if (this.deviceInfo.isMobile && !sessionStorage.getItem("mobileAppBannerClosed")) {
        if (this.deviceInfo.os.toLowerCase() === "ios") {
          this.setState({
            ...this.state,
            showMobileAppBanner: true,
          });
        } else if (this.deviceInfo.os.toLowerCase() === "android") {
          const relatedApps = await navigator.getInstalledRelatedApps();
          if (relatedApps) {
            const psApp = relatedApps.find((app) => app.id === "com.aptihealth.aptihealth");
            if (!psApp) {
              this.setState({
                ...this.state,
                showMobileAppBanner: true,
              });
            }
          }
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  handleCloseMobileBanner = () => {
    this.setState({
      ...this.state,
      showMobileAppBanner: false,
    });
    sessionStorage.setItem("mobileAppBannerClosed", true);
  };

  getIncompleteLogInTasks = (profile) => {
    const logInTasks =
      profile?.tasks?.filter(
        (task) => task.display_log_in && !this.state.skippedLoginTasks.has(task.task_type),
      ) || [];

    return Array.from(logInTasks).sort((a, b) => {
      if (a.rank === null) {
        return 1;
      }
      if (b.rank === null) {
        return -1;
      }
      return b.rank - a.rank;
    });
  };

  renderAcknowledgementLayout = () => {
    return (
      <Layout>
        <BrandedHeader />

        <main className="container tw:p-0 tw:mt-12">
          <Switch>
            <Route
              path="/app/acknowledgments/:policy_name"
              render={(props) => (
                <PolicyAcknowledgement
                  {...props}
                  onAcknowledgementComplete={this.onAcknowledgementComplete}
                  acknowledgements={this.state.acknowledgements}
                  incompleteAcknowledgements={this.state.incompleteAcknowledgements}
                />
              )}
            />

            <Route
              path="/app/log-in-tasks/:task_name"
              render={(props) => {
                // Redirect if no tasks available to prevent undefined errors
                if (!this.state.logInTasks.length) {
                  this.props.history.replace("/app/home");
                  return null;
                }

                return (
                  <LogInTasksRouter
                    task={this.state.logInTasks[0]}
                    onTaskCompleted={this.onTaskCompleted}
                    onTaskSkipped={this.onTaskSkipped}
                    {...props}
                  />
                );
              }}
            />

            <Route
              path="/app/payment-check"
              render={(props) => (
                <PaymentPaywall {...props} onPaywallCompleted={this.onPaywallCompleted} />
              )}
            />
          </Switch>
        </main>
      </Layout>
    );
  };

  renderBaseLayout = () => {
    return (
      <Layout>
        {this.state.showMobileAppBanner && this.deviceInfo && (
          <AppBanner onClose={this.handleCloseMobileBanner} deviceInfo={this.deviceInfo} />
        )}

        <Toolbar
          navbarConfiguration={this.navbarConfiguration}
          toggleHandler={this.toggleSideDrawyer}
          sidebarActive={this.state.showSideDrawyer}
          baseRoute={baseRoute}
          totalUnreadMessages={this.state.totalUnreadMessages}
        />

        <Main2>
          <Switch>
            {/* ===== HOME DASHBOARD ===== */}
            <Route
              path="/app/home"
              render={(props) => (
                <MemberHome {...props} totalUnreadMessages={this.state.totalUnreadMessages} />
              )}
            />

            {/* ===== USER PROFILE & ACCOUNT MANAGEMENT ===== */}
            <Route path="/app/profile" component={Profile} />
            <Route
              path="/app/change-password"
              render={(props) => <ChangePassword {...props} baseRoute="/app" />}
            />
            <Route path="/app/patient-notifications" component={PatientNotifications} />
            <Route path="/app/patient-insurance" component={PatientInsurance} />
            <Route path="/app/patient-payment" component={PaymentsPage} />
            <Route path="/app/mobile-add-payment" component={MobileAddPayment} />

            {/* ===== CARE & HEALTH MANAGEMENT ===== */}
            <Route path="/app/assessment" component={Assessment} />
            <Route
              path="/app/outcome-assessment"
              render={(props) => <Assessment {...props} iteration="post_90_day" />}
            />
            <Route path="/app/phq-9" component={PHQ9} />
            <Route path="/app/gad-7" component={GAD7} />
            <Route path="/app/satisfaction-survey" component={Survey} />
            <Route path="/app/schedule" component={ScheduleRouter} />
            <Route path="/app/teens-and-kids/:step" component={TeensAndKidsCareProgram} />

            {/* ===== COMMUNICATION ===== */}
            <Route path="/app/messages" render={(props) => <MessagesView {...props} />} />
            <Route path="/app/video-call/ratings" component={PostSessionSurveyWrapper} />
            <Route path="/app/video-call" render={() => <VideoClient {...this.props} />} />

            {/* ===== SETTINGS & PREFERENCES ===== */}
            <Route
              path="/app/settings"
              render={(props) => <SettingsPage {...props} baseRoute="/app" />}
            />

            {/* ===== SUPPORT & TECHNICAL ===== */}
            <Route path="/app/tutorial" render={() => <TutorialScreens baseRoute="/app" />} />
            <Route path="/app/system-check" component={SystemCheck} />
            <Route path="/app/av-check" component={AVCheck} />

            {/* ===== LEGAL & COMPLIANCE ===== */}
            <Route
              path="/app/consents"
              render={(props) => {
                if (this.props.isACPEligible && !this.props.isTeensAndKidsProgram)
                  return <PatientConsents {...props} />;

                return <Redirect to="/app" />;
              }}
            />

            <Route
              path="/app/privacy-policy"
              render={(props) => <PrivacyPolicy {...props} baseRoute="/app" />}
            />

            <Route
              path="/app/acknowledgments/:policy_name"
              render={(props) => (
                <PolicyAcknowledgement
                  {...props}
                  onAcknowledgementComplete={this.onAcknowledgementComplete}
                  acknowledgements={this.state.acknowledgements}
                />
              )}
            />

            {/* ===== FALLBACK ===== */}
            <Redirect from="/app" to="/app/home" />
          </Switch>
        </Main2>

        <Footer />
      </Layout>
    );
  };

  render() {
    if (
      this.props.profile &&
      this.props.patientCardInfo.showPaywall !== undefined &&
      !this.state.incompleteAcknowledgements &&
      !this.state.showPaywall &&
      !this.state.logInTasks.length
    ) {
      return <>{this.renderBaseLayout()}</>;
    }

    if (
      this.props.profile &&
      this.props.patientCardInfo.showPaywall !== undefined &&
      (this.state.incompleteAcknowledgements ||
        this.state.showPaywall ||
        this.state.logInTasks.length)
    ) {
      return <>{this.renderAcknowledgementLayout()}</>;
    }

    return <Spinner />;
  }
}

const mapStateToProps = (state) => {
  return {
    assessmentStatus: state.patient.assessmentStatus,
    configs: state.configs,
    flags: state.flags,
    isACPEligible: selectIsACPEligible(state),
    isTeensAndKidsProgram: selectIsTeensAndKidsProgram(state),
    patientCardInfo: state.patientCardInfo,
    profile: state.auth.profile,
    scheduleList: state.patient.scheduleList,
  };
};

export default connect(mapStateToProps, {
  getPatientProfile,
  getAssessmentStatus,
  updateScheduleList,
  fetchPatientPaywall,
})(withRouter(Patient));
